import { fetchApi } from '@app/hooks/api';

export interface ConversationDto {
  id: string;
  userId: string;
  title: string | null;
  primaryAgentId: string | null;
  lastAgentId: string | null;
  lastMessageAt: string;
  messageCount: number;
  isArchived: boolean;
  createdAt: string;
}

export type FeedbackRating = 'up' | 'down';

export interface MessageFeedbackDto {
  rating: FeedbackRating;
  comment?: string;
  submittedAt: string;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  agentId: string | null;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolCalls: unknown[] | null;
  metadata: Record<string, unknown> | null;
  feedback?: MessageFeedbackDto | null;
  createdAt: string;
}

export interface SendMessageResponse {
  userMessage: MessageDto;
  assistantMessage: MessageDto;
}

export async function fetchConversations(
  getToken: () => Promise<string>,
): Promise<ConversationDto[]> {
  return fetchApi<ConversationDto[]>('/api/conversations', getToken);
}

export async function createConversation(
  getToken: () => Promise<string>,
  agentId: string,
  title?: string,
): Promise<ConversationDto> {
  return fetchApi<ConversationDto>('/api/conversations', getToken, {
    method: 'POST',
    body: JSON.stringify({ agentId, title }),
  });
}

export interface MessagePageDto {
  /** Oldest-first within the page. */
  messages: MessageDto[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** Newest page by default; pass the previous `nextCursor` as `before` for older history. */
export async function fetchMessages(
  getToken: () => Promise<string>,
  conversationId: string,
  opts: { limit?: number; before?: string } = {},
): Promise<MessagePageDto> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.before) params.set('before', opts.before);
  const qs = params.toString();
  return fetchApi<MessagePageDto>(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages${qs ? `?${qs}` : ''}`,
    getToken,
  );
}

/** Only the new user text is sent — the server rebuilds the transcript from stored history. */
export async function sendMessage(
  getToken: () => Promise<string>,
  conversationId: string,
  agentId: string,
  text: string,
  promptMode?: string | null,
  model?: string | null,
): Promise<SendMessageResponse> {
  return fetchApi<SendMessageResponse>(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages`,
    getToken,
    { method: 'POST', body: JSON.stringify({ agentId, text, promptMode: promptMode ?? null, model: model ?? null }) },
  );
}

export type StreamEvent =
  | { type: 'token'; delta: string }
  | { type: 'thinking_retract' }
  | { type: 'tool_call'; id: string; name: string; args?: unknown }
  | {
      type: 'tool_result';
      tool_call_id: string;
      name: string;
      result: string;
      preview: string;
      status?: 'success' | 'error';
    }
  | { type: 'status'; elapsed_s: number | null }
  | { type: 'done'; userMessage: MessageDto; assistantMessage: MessageDto }
  | { type: 'error'; detail: string; code?: string };

export async function sendMessageStream(
  getToken: () => Promise<string>,
  conversationId: string,
  agentId: string,
  text: string,
  promptMode: string | null,
  model: string | null,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = await getToken();
  const response = await fetch(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages/stream`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ agentId, text, promptMode, model }),
      signal,
    },
  );

  if (!response.ok || !response.body) {
    throw new Error(`HTTP_${response.status}: ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIdx: number;
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      const dataLines = rawEvent
        .split('\n')
        .filter((l) => l.startsWith('data: '))
        .map((l) => l.slice(6));
      if (dataLines.length === 0) continue;
      const json = dataLines.join('\n');
      let event: StreamEvent;
      try {
        event = JSON.parse(json) as StreamEvent;
      } catch (parseErr) {
        // Surface but don't abort — server may emit stray comments/keepalives.
        console.error('[stream] failed to parse SSE event:', json, parseErr);
        continue;
      }
      onEvent(event);
      if (event.type === 'error') {
        const streamErr = new Error(event.detail);
        // getFriendlyErrorMessage() keys off this to pick the user-facing text.
        (streamErr as any).code = event.code ?? 'unknown';
        throw streamErr;
      }
    }
  }
}

export async function deleteConversation(
  getToken: () => Promise<string>,
  conversationId: string,
): Promise<void> {
  await fetchApi(`/api/conversations/${encodeURIComponent(conversationId)}`, getToken, {
    method: 'DELETE',
  });
}

export async function submitFeedback(
  getToken: () => Promise<string>,
  conversationId: string,
  messageId: string,
  rating: FeedbackRating,
  comment?: string,
): Promise<MessageFeedbackDto> {
  return fetchApi<MessageFeedbackDto>(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    getToken,
    { method: 'PUT', body: JSON.stringify({ rating, comment }) },
  );
}

export async function renameConversation(
  getToken: () => Promise<string>,
  conversationId: string,
  title: string,
): Promise<void> {
  await fetchApi(`/api/conversations/${encodeURIComponent(conversationId)}`, getToken, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}
